import { Reveal } from "@/components/Reveal";
import { PageHero, Overline, SectionShell } from "@/components/primitives";
import AuthorityFramework from "@/sections/AuthorityFramework";
import FinalCTA from "@/sections/FinalCTA";
import { IMAGES } from "@/data/content";

const NOTS = ["We do not own publications.", "We do not own conferences.", "We do not sell advertising."];

const CONTRIBUTIONS = [
  "Every publication.", "Every executive interview.", "Every conference.",
  "Every strategic partnership.", "Every award.", "Every thought leadership opportunity.",
  "Every executive community.",
];

export default function WhyHMBeacon() {
  return (
    <div data-testid="why-page">
      <PageHero
        label="Why HM Beacon"
        lines={["We build authority,", "not just visibility."]}
        intro="HM Beacon helps organizations build long-term authority by strategically positioning them within the ecosystems where industries are shaped."
      />

      {/* What we are not */}
      <SectionShell className="py-24 md:py-36 border-b hairline">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal><Overline>The Distinction</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-heading font-light text-navy text-3xl md:text-4xl leading-snug">
                We are not a media company, a PR agency, or an advertising business.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="border-t hairline">
              {NOTS.map((n, i) => (
                <Reveal key={n} delay={i * 0.08}>
                  <p className="py-6 border-b hairline font-heading font-light text-navy text-2xl md:text-3xl">{n}</p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}>
              <p className="mt-10 text-lg leading-relaxed text-ink/75 font-body max-w-xl">
                Instead, we understand where influence exists and help organizations become visible
                where business decisions are made.
              </p>
            </Reveal>
          </div>
        </div>
      </SectionShell>

      {/* Core philosophy */}
      <section className="py-28 md:py-40 bg-mist/50 border-b hairline">
        <SectionShell>
          <div className="max-w-4xl">
            <Reveal><Overline>The Philosophy</Overline></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-10 font-heading font-light text-navy text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
                Markets reward organizations that become visible to the{" "}
                <span className="text-gold">right people</span> — not necessarily the most people.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-12 max-w-2xl text-lg md:text-xl leading-relaxed text-ink/70 font-body">
                Authority is earned through relevance, consistency, relationships, and strategic
                positioning — not volume.
              </p>
            </Reveal>
          </div>
        </SectionShell>
      </section>

      {/* Contributions -> one objective */}
      <SectionShell className="py-24 md:py-36 border-b hairline">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="border-t hairline">
              {CONTRIBUTIONS.map((c, i) => (
                <Reveal key={c} delay={i * 0.05}>
                  <p className="py-5 border-b hairline font-heading font-light text-navy/80 text-xl md:text-2xl">{c}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:sticky lg:top-32">
            <Reveal delay={0.1}>
              <p className="overline text-navy/50">Should contribute toward one objective</p>
              <h2 className="mt-6 font-heading font-light text-navy text-5xl md:text-7xl leading-none">
                Building<br />Authority.
              </h2>
              <div className="mt-10 relative overflow-hidden aspect-[16/10]">
                <img src={IMAGES.boardroom} alt="Boardroom" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>
      </SectionShell>

      <AuthorityFramework />

      <FinalCTA />
    </div>
  );
}
